import {
  edgelessToolbarI18nT,
  ThemeProvider,
} from '@blocksuite/affine-shared/services';
import {
  EdgelessToolbarToolMixin,
  SENIOR_TOOL_ART,
} from '@blocksuite/affine-widget-edgeless-toolbar';
import { SignalWatcher } from '@blocksuite/global/lit';
import { css, html, LitElement } from 'lit';

import { FrameTool } from '../frame-tool';

/** The frame tool in the dock: a stack of frames in the formats its menu offers. */
export class EdgelessFrameToolButton extends EdgelessToolbarToolMixin(
  SignalWatcher(LitElement)
) {
  static override styles = [
    SENIOR_TOOL_ART,
    css`
      :host,
      .edgeless-frame-button {
        display: block;
        width: 100%;
        height: 100%;
      }

      .frame-sheet {
        border-radius: 3px;
      }
      /* A frame's title sits above its top left corner. */
      .frame-sheet::before {
        content: '';
        position: absolute;
        left: -1px;
        top: -8px;
        width: 16px;
        height: 5px;
        border-radius: 2.5px;
        background: var(--art-ink);
      }
      .frame-sheet.wide {
        width: 56px;
        height: 32px;
        --x: -14px;
        --y: -16px;
        --r: -7deg;
      }
      .frame-sheet.standard {
        width: 48px;
        height: 36px;
        --x: 15px;
        --y: -11px;
        --r: 6deg;
      }
      .frame-sheet.square {
        width: 40px;
        height: 40px;
        --y: 9px;
        border-color: var(--streamlain-v2-brand-primary);
      }
      .frame-sheet.square::before {
        background: var(--streamlain-v2-brand-primary);
      }
      .frame-handle {
        position: absolute;
        top: -4px;
        width: 6px;
        height: 6px;
        border-radius: 1.5px;
        background: var(--art-card);
        border: 1px solid var(--streamlain-v2-brand-primary);
      }
      .frame-handle.start {
        left: -4px;
      }
      .frame-handle.end {
        right: -4px;
      }

      .senior-art:hover .frame-sheet.wide,
      .senior-art.open .frame-sheet.wide {
        --x: -23px;
        --y: -31px;
        --r: -13deg;
      }
      .senior-art:hover .frame-sheet.standard,
      .senior-art.open .frame-sheet.standard {
        --x: 24px;
        --y: -27px;
        --r: 12deg;
      }
      .senior-art:hover .frame-sheet.square,
      .senior-art.open .frame-sheet.square {
        --y: -7px;
      }
    `,
  ];

  override enableActiveBackground = true;

  override type = FrameTool;

  private _toggleFrameMenu() {
    if (this.tryDisposePopper()) return;

    const menu = this.createPopper('edgeless-frame-menu', this);
    menu.element.edgeless = this.edgeless;
  }

  override render() {
    const appTheme = this.edgeless.std.get(ThemeProvider).app$.value;

    return html`<edgeless-toolbar-button
      class="edgeless-frame-button"
      .tooltip=${this.popper
        ? ''
        : html`<affine-tooltip-content-with-shortcut
            data-tip="${edgelessToolbarI18nT('toolbar.frame')}"
            data-shortcut="${'F'}"
          ></affine-tooltip-content-with-shortcut>`}
      .tooltipOffset=${5}
      @click=${() => {
        // don't update tool before toggling menu
        this._toggleFrameMenu();
        this.setEdgelessTool(FrameTool);
      }}
    >
      <div
        class="senior-art ${this.popper ? 'open' : ''}"
        data-app-theme=${appTheme}
      >
        <div class="art-part art-card frame-sheet wide">
          <svg viewBox="0 0 56 32" fill="none">
            <path
              d="M8 22l9-9 7 7 6-5 10 9"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </div>
        <div class="art-part art-card frame-sheet standard">
          <svg viewBox="0 0 48 36" fill="none">
            <rect
              x="8"
              y="9"
              width="14"
              height="10"
              rx="2"
              fill="currentColor"
            />
            <path
              d="M27 11h13M27 17h9M8 26h32"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
            />
          </svg>
        </div>
        <div class="art-part art-card frame-sheet square">
          <svg viewBox="0 0 40 40" fill="none">
            <circle cx="13" cy="14" r="5" fill="currentColor" />
            <rect
              x="21"
              y="9"
              width="11"
              height="10"
              rx="2"
              fill="currentColor"
            />
          </svg>
          <i class="frame-handle start"></i>
          <i class="frame-handle end"></i>
        </div>
      </div>
    </edgeless-toolbar-button>`;
  }
}
